import React, { Component } from 'react';
import { Button, Form, Row, Col, Nav, Tab, Alert } from 'react-bootstrap';
import { FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, IconButton, Autocomplete } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import EventIcon from '@mui/icons-material/Event';
import moment from 'moment';
import toastr from 'toastr';
import { 
    getAllMedications,
    addPatientMedication,
    updatePatientMedication,
    verifyPatientMedication,
    discontinuePatientMedication
} from '../../../../services/clinicPortalServices/patientMedicationService';
import { fetchPatientWithQuery } from '../../../../services/clinicPortalServices/patientSearchService';
import { getUserDetails } from '../../../../services/common/util';

const dosageUnits = [
    { value: 'mg', label: 'mg' },
    { value: 'mcg', label: 'mcg' },
    { value: 'g', label: 'g' },
    { value: 'ml', label: 'ml' },
    { value: 'units', label: 'units' },
    { value: 'IU', label: 'IU' },
    { value: 'mEq', label: 'mEq' },
    { value: 'mmol', label: 'mmol' }
];

const routes = [
    { value: 'oral', label: 'Oral' },
    { value: 'IV', label: 'Intravenous (IV)' },
    { value: 'IM', label: 'Intramuscular (IM)' },
    { value: 'SC', label: 'Subcutaneous (SC)' },
    { value: 'topical', label: 'Topical' },
    { value: 'inhaled', label: 'Inhaled' },
    { value: 'nasal', label: 'Nasal' },
    { value: 'ophthalmic', label: 'Ophthalmic' },
    { value: 'otic', label: 'Otic' },
    { value: 'rectal', label: 'Rectal' },
    { value: 'vaginal', label: 'Vaginal' },
    { value: 'transdermal', label: 'Transdermal' },
    { value: 'other', label: 'Other' }
];

const frequencies = [
    { value: 'daily', label: 'Once Daily' },
    { value: 'bid', label: 'Twice Daily (BID)' },
    { value: 'tid', label: 'Three Times Daily (TID)' },
    { value: 'qid', label: 'Four Times Daily (QID)' },
    { value: 'q4h', label: 'Every 4 Hours' },
    { value: 'q6h', label: 'Every 6 Hours' },
    { value: 'q8h', label: 'Every 8 Hours' },
    { value: 'q12h', label: 'Every 12 Hours' },
    { value: 'prn', label: 'As Needed (PRN)' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'other', label: 'Other' }
];

export default class PatientMedicationDetails extends Component {
    constructor(props) {
        super(props);
        
        const medication = props.medication || {};
        const isNew = props.isNew || !medication._id;
        
        this.state = {
            isNew,
            isLoading: false,
            medications: [],
            patientOptions: [],
            
            // Form fields
            patientId: medication.patientId?._id || props.patientId || '',
            selectedPatient: null,
            medicationId: medication.medicationId?._id || '',
            selectedMedication: null,
            dosage: medication.dosage || '',
            dosageUnit: medication.dosageUnit || 'mg',
            frequency: medication.frequency || 'bid',
            route: medication.route || 'oral',
            startDate: medication.startDate ? new Date(medication.startDate) : new Date(),
            endDate: medication.endDate ? new Date(medication.endDate) : null,
            prescriber: {
                name: medication.prescriber?.name || '',
                npi: medication.prescriber?.npi || '',
                phone: medication.prescriber?.phone || ''
            },
            instructions: medication.instructions || '',
            indication: medication.indication || '',
            notes: medication.notes || '',
            
            // Status fields
            isActive: medication.isActive !== undefined ? medication.isActive : true,
            isVerified: medication.isVerified || false,
            discontinuationReason: '',
            
            // Validation
            errors: {},
            activeTab: 'basic'
        };
    }

    componentDidMount() {
        this.loadMedications();
        if (!this.props.patientId && this.state.isNew) {
            this.loadPatientOptions();
        }
        if (this.state.patientId && !this.state.selectedPatient) {
            this.loadPatientById(this.state.patientId);
        }
    }

    loadMedications = () => {
        getAllMedications()
            .then((response) => {
                const options = response.data.map(med => ({
                    value: med._id,
                    label: `${med.name} ${med.strength || ''}`
                }));
                this.setState({ medications: options });
                
                // Set selected medication if editing
                if (this.props.medication?.medicationId) {
                    const selected = options.find(opt => opt.value === this.props.medication.medicationId._id);
                    this.setState({ selectedMedication: selected });
                }
            })
            .catch((error) => {
                console.error('Error loading medications:', error);
                toastr.error('Error loading medications');
            });
    };

    loadPatientOptions = () => {
        fetchPatientWithQuery({})
            .then((response) => {
                const options = response.data.map(patient => ({
                    value: patient._id,
                    label: `${patient.first_name} ${patient.last_name} (MRN: ${patient.mrn})`
                }));
                this.setState({ patientOptions: options });
            })
            .catch((error) => {
                console.error('Error loading patients:', error);
            });
    };

    loadPatientById = (patientId) => {
        fetchPatientWithQuery({ _id: patientId })
            .then((response) => {
                if (response.data && response.data.length > 0) {
                    const patient = response.data[0];
                    const patientOption = {
                        value: patient._id,
                        label: `${patient.first_name} ${patient.last_name} (MRN: ${patient.mrn})`
                    };
                    this.setState({ 
                        selectedPatient: patientOption,
                        patientOptions: [patientOption]
                    });
                }
            })
            .catch((error) => {
                console.error('Error loading patient:', error);
            });
    };


    handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            this.setState(prevState => ({
                [parent]: {
                    ...prevState[parent],
                    [child]: value
                }
            }));
        } else {
            this.setState({ [name]: value });
        }
    };

    handleDateChange = (date, fieldName) => {
        this.setState({ [fieldName]: date });
    };

    validateForm = () => {
        const errors = {};
        
        if (!this.state.patientId) {
            errors.patientId = 'Patient is required';
        }
        if (!this.state.medicationId) {
            errors.medicationId = 'Medication is required';
        }
        if (!this.state.dosage) {
            errors.dosage = 'Dosage is required';
        }
        if (!this.state.startDate) {
            errors.startDate = 'Start date is required';
        }
        
        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleSubmit = (e) => {
        e.preventDefault();
        
        if (!this.validateForm()) {
            toastr.error('Please fill in all required fields');
            return;
        }

        this.setState({ isLoading: true });

        const medicationData = {
            patientId: this.state.patientId,
            medicationId: this.state.medicationId,
            dosage: parseFloat(this.state.dosage),
            dosageUnit: this.state.dosageUnit,
            frequency: this.state.frequency,
            route: this.state.route,
            startDate: this.state.startDate,
            endDate: this.state.endDate,
            prescriber: this.state.prescriber,
            instructions: this.state.instructions,
            indication: this.state.indication,
            notes: this.state.notes,
            isActive: this.state.isActive
        };

        const savePromise = this.state.isNew
            ? addPatientMedication(medicationData)
            : updatePatientMedication(this.props.medication._id, medicationData);

        savePromise
            .then((response) => {
                toastr.success(`Medication ${this.state.isNew ? 'added' : 'updated'} successfully`);
                if (this.props.refreshGrid) {
                    this.props.refreshGrid();
                }
                this.props.handleClose();
            })
            .catch((error) => {
                console.error('Error saving medication:', error);
                toastr.error(`Error ${this.state.isNew ? 'adding' : 'updating'} medication`);
            })
            .finally(() => {
                this.setState({ isLoading: false });
            });
    };

    handleVerify = () => {
        if (!this.props.medication?._id) return;

        const verificationData = {
            verificationNotes: `Verified by ${JSON.parse(getUserDetails()).user_name}`
        };

        this.setState({ isLoading: true });
        verifyPatientMedication(this.props.medication._id, verificationData)
            .then((response) => {
                toastr.success('Medication verified successfully');
                if (this.props.refreshGrid) {
                    this.props.refreshGrid();
                }
                this.props.handleClose();
            })
            .catch((error) => {
                console.error('Error verifying medication:', error);
                toastr.error('Error verifying medication');
            })
            .finally(() => {
                this.setState({ isLoading: false });
            });
    };

    handleDiscontinue = () => {
        if (!this.props.medication?._id) return;
        if (!this.state.discontinuationReason) {
            toastr.error('Please provide a reason for discontinuation');
            return;
        }

        const discontinueData = {
            reason: this.state.discontinuationReason
        };

        this.setState({ isLoading: true });
        discontinuePatientMedication(this.props.medication._id, discontinueData)
            .then((response) => {
                toastr.success('Medication discontinued successfully');
                if (this.props.refreshGrid) {
                    this.props.refreshGrid();
                }
                this.props.handleClose();
            })
            .catch((error) => {
                console.error('Error discontinuing medication:', error);
                toastr.error('Error discontinuing medication');
            })
            .finally(() => {
                this.setState({ isLoading: false });
            });
    };

    render() {
        const { isNew, errors } = this.state;
        const isActive = this.props.medication?.status === 'active';
        const isVerified = this.props.medication?.isVerified;

        return (
            <Form onSubmit={this.handleSubmit} className="medication-details-form">
                <Tab.Container activeKey={this.state.activeTab}>
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item>
                            <Nav.Link 
                                eventKey="basic" 
                                onClick={() => this.setState({ activeTab: 'basic' })}
                            >
                                Basic Information
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                eventKey="prescriber" 
                                onClick={() => this.setState({ activeTab: 'prescriber' })}
                            >
                                Prescriber
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                eventKey="instructions" 
                                onClick={() => this.setState({ activeTab: 'instructions' })}
                            >
                                Instructions & Notes
                            </Nav.Link>
                        </Nav.Item>
                        {!isNew && (
                            <Nav.Item>
                                <Nav.Link 
                                    eventKey="actions" 
                                    onClick={() => this.setState({ activeTab: 'actions' })}
                                >
                                    Actions
                                </Nav.Link>
                            </Nav.Item>
                        )}
                    </Nav>

                    <Tab.Content>
                        <Tab.Pane eventKey="basic">
                            <Row>
                                {(!this.props.patientId || isNew) && (
                                    <Col md={12}>
                                        <Autocomplete
                                            value={this.state.selectedPatient}
                                            onChange={(event, newValue) => {
                                                const selectedId = newValue ? newValue.value : '';
                                                this.setState({ 
                                                    selectedPatient: newValue, 
                                                    patientId: selectedId 
                                                });
                                            }}
                                            options={this.state.patientOptions}
                                            getOptionLabel={(option) => option.label || ''}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            disabled={!isNew}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={<>Patient <span className="text-danger">*</span></>}
                                                    error={!!errors.patientId}
                                                    helperText={errors.patientId}
                                                    margin="normal"
                                                    fullWidth
                                                />
                                            )}
                                            filterOptions={(options, params) => {
                                                const filtered = options.filter(option => 
                                                    option.label.toLowerCase().includes(params.inputValue.toLowerCase())
                                                );
                                                return filtered;
                                            }}
                                            sx={{ marginTop: 2 }}
                                        />
                                    </Col>
                                )}
                                
                                <Col md={12}>
                                    <Autocomplete
                                        value={this.state.selectedMedication}
                                        onChange={(event, newValue) => {
                                            const selectedId = newValue ? newValue.value : '';
                                            this.setState({ 
                                                selectedMedication: newValue, 
                                                medicationId: selectedId 
                                            });
                                        }}
                                        options={this.state.medications}
                                        getOptionLabel={(option) => option.label || ''}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={<>Medication <span className="text-danger">*</span></>}
                                                error={!!errors.medicationId}
                                                helperText={errors.medicationId}
                                                margin="normal"
                                                fullWidth
                                            />
                                        )}
                                        filterOptions={(options, params) => {
                                            const filtered = options.filter(option => 
                                                option.label.toLowerCase().includes(params.inputValue.toLowerCase())
                                            );
                                            return filtered;
                                        }}
                                        sx={{ marginTop: 2 }}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Dosage <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="dosage"
                                            value={this.state.dosage}
                                            onChange={this.handleInputChange}
                                            isInvalid={!!errors.dosage}
                                            step="0.01"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.dosage}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Dosage Unit</Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="dosageUnit"
                                            value={this.state.dosageUnit}
                                            onChange={this.handleInputChange}
                                        >
                                            {dosageUnits.map(unit => (
                                                <option key={unit.value} value={unit.value}>
                                                    {unit.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Frequency</Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="frequency"
                                            value={this.state.frequency}
                                            onChange={this.handleInputChange}
                                        >
                                            {frequencies.map(freq => (
                                                <option key={freq.value} value={freq.value}>
                                                    {freq.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Route</Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="route"
                                            value={this.state.route}
                                            onChange={this.handleInputChange}
                                        >
                                            {routes.map(route => (
                                                <option key={route.value} value={route.value}>
                                                    {route.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Start Date <span className="text-danger">*</span>
                                        </Form.Label>
                                        <MobileDatePicker
                                            value={this.state.startDate}
                                            onChange={(dateTime) => this.handleDateChange(dateTime?.$d || null, 'startDate')}
                                            maxDate={new Date()}
                                            label="Start Date"
                                            renderInput={(props) => (
                                                <TextField
                                                    {...props}
                                                    fullWidth
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton edge="end">
                                                                    <EventIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    error={!!errors.startDate}
                                                    helperText={errors.startDate}
                                                />
                                            )}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>End Date</Form.Label>
                                        <MobileDatePicker
                                            value={this.state.endDate}
                                            onChange={(dateTime) => this.handleDateChange(dateTime?.$d || null, 'endDate')}
                                            minDate={this.state.startDate}
                                            label="End Date"
                                            clearable
                                            renderInput={(props) => (
                                                <TextField
                                                    {...props}
                                                    fullWidth
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton edge="end">
                                                                    <EventIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Tab.Pane>

                        <Tab.Pane eventKey="prescriber">
                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Prescriber Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="prescriber.name"
                                            value={this.state.prescriber.name}
                                            onChange={this.handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>NPI</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="prescriber.npi"
                                            value={this.state.prescriber.npi}
                                            onChange={this.handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="prescriber.phone"
                                            value={this.state.prescriber.phone}
                                            onChange={this.handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Tab.Pane>

                        <Tab.Pane eventKey="instructions">
                            <Row>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Instructions</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="instructions"
                                            value={this.state.instructions}
                                            onChange={this.handleInputChange}
                                            placeholder="Special instructions for taking this medication..."
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Indication</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="indication"
                                            value={this.state.indication}
                                            onChange={this.handleInputChange}
                                            placeholder="Reason for taking this medication..."
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Notes</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="notes"
                                            value={this.state.notes}
                                            onChange={this.handleInputChange}
                                            placeholder="Additional notes..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Tab.Pane>

                        {!isNew && (
                            <Tab.Pane eventKey="actions">
                                <Row>
                                    {!isVerified && isActive && (
                                        <Col md={12}>
                                            <Alert variant="info">
                                                <h5>Verify Medication</h5>
                                                <p>This medication has not been verified yet.</p>
                                                <Button 
                                                    variant="primary" 
                                                    onClick={this.handleVerify}
                                                    disabled={this.state.isLoading}
                                                >
                                                    Verify Medication
                                                </Button>
                                            </Alert>
                                        </Col>
                                    )}

                                    {isActive && (
                                        <Col md={12}>
                                            <Alert variant="warning">
                                                <h5>Discontinue Medication</h5>
                                                <Form.Group>
                                                    <Form.Label>Reason for Discontinuation</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        name="discontinuationReason"
                                                        value={this.state.discontinuationReason}
                                                        onChange={this.handleInputChange}
                                                        placeholder="Please provide a reason..."
                                                    />
                                                </Form.Group>
                                                <Button 
                                                    variant="danger" 
                                                    onClick={this.handleDiscontinue}
                                                    disabled={this.state.isLoading || !this.state.discontinuationReason}
                                                >
                                                    Discontinue Medication
                                                </Button>
                                            </Alert>
                                        </Col>
                                    )}

                                    {this.props.medication?.status === 'discontinued' && (
                                        <Col md={12}>
                                            <Alert variant="danger">
                                                <h5>Medication Discontinued</h5>
                                                <p>
                                                    Discontinued on:{' '}
                                                    {moment(this.props.medication.discontinuedDate).format('MM/DD/YYYY')}
                                                </p>
                                                {this.props.medication.discontinuationReason && (
                                                    <p>Reason: {this.props.medication.discontinuationReason}</p>
                                                )}
                                            </Alert>
                                        </Col>
                                    )}
                                </Row>
                            </Tab.Pane>
                        )}
                    </Tab.Content>
                </Tab.Container>

                {!this.props.hideFooter && (
                    <div className="mt-4 pt-3 border-top d-flex justify-content-between">
                        <Button variant="secondary" onClick={this.props.handleClose}>
                            Cancel
                        </Button>
                        {(isNew || isActive) && (
                            <Button 
                                variant="primary" 
                                type="submit" 
                                disabled={this.state.isLoading}
                            >
                                {this.state.isLoading ? 'Saving...' : (isNew ? 'Add Medication' : 'Update Medication')}
                            </Button>
                        )}
                    </div>
                )}
            </Form>
        );
    }
}